// PROD-2743 · design sheet v1.1, §1 global rules, as the ONE set of class recipes the app screens
// share when the `frameV2` switch is ON (see `useFrameV2`). Every value here is a Signal DS token
// utility or a DS type role (`TEXT_ROLE_CLASSES` / `TEXT_TONE_CLASSES`); nothing is a raw hex, px or
// arbitrary value. A screen reaches for these instead of composing its own, so a page title, a card
// or a caps label reads the same on the dashboard, the patients list, the chart and settings.
//
// None of these is applied with the switch OFF (since 2 Nov only the dev fallback `?x=frame-1`). Callers choose between
// today's classes and these.
import { cn, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** Page title: `--text-lg` (24) semibold, text-primary. Never brand ink on a heading. */
export const V2_PAGE_TITLE = 'text-lg font-semibold text-text-primary';

/** Section / panel title: `--text-md` (18) semibold, text-primary. */
export const V2_PANEL_TITLE = TEXT_ROLE_CLASSES['panel-title'];

/** Card title / entity name: 15 semibold, text-primary. */
export const V2_CARD_TITLE = TEXT_ROLE_CLASSES['card-title'];

/** Body (13) and its emphasised value form. */
export const V2_BODY = TEXT_ROLE_CLASSES.body;
export const V2_BODY_EMPHASIS = TEXT_ROLE_CLASSES['body-emphasis'];

/** Caps label (12 bold, uppercase, section-heading tracking) in text-secondary: labels whisper. */
export const V2_EYEBROW = cn(
  TEXT_ROLE_CLASSES['section-eyebrow'],
  'uppercase tracking-section-heading',
  TEXT_TONE_CLASSES.secondary,
);

/** Meta (11) in text-secondary, for a label or fact that carries information (MRN, dates). */
export const V2_META = cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary);

/** Counts and statuses at a glance (11 bold) in text-secondary. */
export const V2_META_STRONG = cn(TEXT_ROLE_CLASSES['meta-strong'], TEXT_TONE_CLASSES.secondary);

/** A card on the canvas: white, the card border, `--shadow-2` at rest. Radius is never changed. */
export const V2_CARD_SURFACE = 'bg-surface-card border border-border-card shadow-2';

/** The card-border + elevation swap for a surface that already sets its own background. */
export const V2_CARD_EDGE = 'border border-border-card shadow-2';

/** The page itself sits on the canvas. */
export const V2_CANVAS = 'bg-surface-background';

/** An idle destructive icon is neutral; red only on hover / focus (status colour is for values). */
export const V2_IDLE_DESTRUCTIVE_ICON =
  'text-text-secondary hover:text-status-error focus-visible:text-status-error';

/** True when this browser has the `frameV2` switch on. For deep leaf markup where threading the
 *  `useFrameV2` hook would mean restructuring a component; it reads the same stored switch. */
export function isFrameV2(): boolean {
  return isEncounterFlagOn('frameV2', { ownSwitchOnly: true });
}

/**
 * The switch-ON class swap for one element: ON merges `v2` over today's classes through the DS
 * `cn` (tailwind-merge taught the DS scale, so `border-border-default` → `border-border-card`,
 * `shadow-sm` → `shadow-2`, `text-brand-depth` → the role's ink). OFF returns `today` UNCHANGED,
 * character for character, so the default path's DOM does not move.
 */
export function frameV2Swap(today: string, v2: string): string {
  return isFrameV2() ? cn(today, v2) : today;
}

/**
 * The design (V2) look, unconditional: merges `v2` over `today` through the DS `cn`. Outside the
 * encounter the look never depends on a switch; switches choose behaviour only.
 */
export function v2Look(today: string, v2: string): string {
  return cn(today, v2);
}
